/**
 * PublishGateFindings — tier-2 findings block rendered INSIDE DeployWebPlacementDialog
 * (DC-21, AC-016, AC-027, AC-052, AC-062). Modal depth stays at one: no nested dialog.
 * Each finding names WHERE its button lands (routing class tag, DC-16); two-target
 * findings get two buttons; class X renders an external link + vendor path (DC-13).
 */
import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useTheme, type Theme } from '@mui/material/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type {
  ActivationAction,
  ActivationActionClass,
  PublishGateFinding,
} from '@modules/shared-types';
import { LAUNCH_ACTIVATION_TEST_IDS } from '../testIds';
import { VendorPath } from './VendorPath';

export interface PublishGateFindingsProps {
  findings: PublishGateFinding[];
  /** The check could not run (AC-062): render the explicit unavailable row, never a clean state. */
  unavailable?: boolean;
  onAction: (finding: PublishGateFinding, action: ActivationAction, actionIndex: number) => void;
  onOpenArticle?: (articleAlias: string) => void;
}

/** Where the button lands — the honest part of DC-16. */
export const ROUTING_TAG: Record<ActivationActionClass, { tag: string; hint: string }> = {
  P: { tag: 'page', hint: 'the target will be highlighted' },
  C: { tag: 'canvas', hint: 'the target will be highlighted' },
  D: { tag: 'here', hint: 'opens in place, no navigation' },
  X: { tag: 'external', hint: 'leaves the app — copy the value back into the field named below' },
};

export function countGateFindings(
  findings: PublishGateFinding[]
): { errors: number; warnings: number; notEvaluated: number } {
  return {
    errors: findings.filter((f) => f.severity === 'error').length,
    warnings: findings.filter((f) => f.severity === 'warning').length,
    notEvaluated: findings.filter((f) => f.severity === 'not_evaluated').length,
  };
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

function severityStyle(theme: Theme, severity: PublishGateFinding['severity']) {
  switch (severity) {
    case 'error':
      return { border: theme.palette.error.main, Icon: ErrorOutlineIcon, color: theme.palette.error.main };
    case 'warning':
      return { border: theme.palette.warning.main, Icon: WarningAmberIcon, color: theme.palette.warning.main };
    default:
      return { border: theme.palette.divider, Icon: RemoveCircleOutlineIcon, color: theme.palette.text.disabled };
  }
}

export const PublishGateFindings: React.FC<PublishGateFindingsProps> = ({
  findings,
  unavailable = false,
  onAction,
  onOpenArticle,
}) => {
  const theme = useTheme();
  const T = LAUNCH_ACTIVATION_TEST_IDS;
  const counts = countGateFindings(findings);

  if (findings.length === 0 && !unavailable) {
    return (
      <Box
        data-testid={T.gateClean}
        sx={{
          display: 'flex',
          gap: 1.5,
          p: 1.5,
          border: `1px solid ${theme.palette.success.main}`,
          borderRadius: 1.5,
        }}
      >
        <CheckCircleOutlineIcon fontSize="small" sx={{ color: theme.palette.success.main }} />
        <Box>
          <Typography variant="body2" fontWeight={500}>
            Pre-publish check passed
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Conditions reference live options, Stripe modes match, attribution is configured.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '.06em' }}>
        Pre-publish check
      </Typography>
      <Typography data-testid={T.gateSummary} variant="caption" color="text.secondary">
        {[
          counts.errors > 0 ? plural(counts.errors, 'blocking', 'blocking') : null,
          counts.warnings > 0 ? plural(counts.warnings, 'warning', 'warnings') : null,
          counts.notEvaluated > 0 ? `${counts.notEvaluated} not evaluated` : null,
          unavailable ? 'check unavailable' : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        {' — for this version and this placement.'}
      </Typography>

      {unavailable && (
        <Box
          data-testid={T.gateUnavailable}
          sx={{
            display: 'flex',
            gap: 1.5,
            p: 1.5,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1.5,
            color: theme.palette.text.secondary,
          }}
        >
          <RemoveCircleOutlineIcon fontSize="small" sx={{ color: theme.palette.text.disabled }} />
          <Box>
            <Typography variant="body2" fontWeight={500}>
              The publish check could not run
            </Typography>
            <Typography variant="caption">
              Publishing is still allowed; findings are unknown.
            </Typography>
          </Box>
        </Box>
      )}

      {findings.map((finding) => {
        const style = severityStyle(theme, finding.severity);
        const muted = finding.severity === 'not_evaluated';
        const StyleIcon = style.Icon;
        const firstAction = finding.actions[0];
        return (
          <Box
            key={`${finding.id}-${finding.screenId ?? ''}-${finding.title}`}
            data-testid={T.gateFinding(finding.id)}
            data-severity={finding.severity}
            sx={{
              p: theme.spacing(1.5, 1.75),
              border: `1px solid ${style.border}`,
              borderRadius: 1.5,
              color: muted ? theme.palette.text.secondary : theme.palette.text.primary,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <StyleIcon fontSize="small" sx={{ color: style.color, mt: '2px' }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={500}>
                  {finding.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {finding.detail}
                </Typography>
                {muted && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                    Not checked
                  </Typography>
                )}
              </Box>
            </Box>

            {finding.actions.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 1.25, ml: 4 }}>
                {finding.actions.map((action, index) =>
                  action.class === 'X' && action.externalUrl ? (
                    <Button
                      key={`${action.label}-${index}`}
                      data-testid={T.gateFindingAction(finding.id, index)}
                      component={Link}
                      href={action.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      variant="outlined"
                      endIcon={<OpenInNewIcon fontSize="small" />}
                      sx={{ textTransform: 'none' }}
                    >
                      {action.label}
                    </Button>
                  ) : (
                    <Button
                      key={`${action.label}-${index}`}
                      data-testid={T.gateFindingAction(finding.id, index)}
                      size="small"
                      variant="outlined"
                      onClick={() => onAction(finding, action, index)}
                      sx={{ textTransform: 'none' }}
                    >
                      {action.label}
                    </Button>
                  )
                )}
                {firstAction && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
                  >
                    <Box
                      component="span"
                      sx={{
                        px: 0.75,
                        borderRadius: 0.5,
                        border: `1px solid ${theme.palette.divider}`,
                        fontWeight: 500,
                      }}
                    >
                      {ROUTING_TAG[firstAction.class].tag}
                    </Box>
                    {ROUTING_TAG[firstAction.class].hint}
                  </Typography>
                )}
                {firstAction?.articleAlias && (
                  <Link
                    data-testid={T.gateArticle(finding.id)}
                    component="button"
                    type="button"
                    variant="caption"
                    onClick={() => onOpenArticle?.(firstAction.articleAlias as string)}
                    sx={{ ml: 'auto' }}
                  >
                    {/* D4/AM-4: the alias is an internal id — never printed in user-visible copy. */}
                    Read the guide →
                  </Link>
                )}
              </Box>
            )}

            {finding.actions.some((a) => a.vendorPath && a.vendorPath.length > 0) && (
              <Box sx={{ ml: 4 }}>
                <VendorPath
                  data-testid={T.gateVendorPath(finding.id)}
                  steps={finding.actions.flatMap((a) => a.vendorPath ?? [])}
                />
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default PublishGateFindings;
